import { useEffect, useState } from "react";
import { GitMerge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const who = (e) => [e.contact?.name, e.contact?.email, e.contact?.mobile].filter(Boolean).join(" · ") || "(no contact details)";

const Candidate = ({ e, picked, onPick }) => (
  <button type="button" onClick={() => onPick(e)} data-testid={`enquiry-merge-candidate-${e.id}`}
    className={`w-full rounded-md border p-2.5 text-left text-sm transition-colors ${picked ? "border-blue-500 bg-blue-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-zinc-900">{who(e)}</span>
      {e.match && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-2xs uppercase tracking-wider text-amber-800">{e.match}</span>}
    </div>
    <div className="text-xs text-zinc-500">{fmtDateTime(e.created_at)} · {e.source_channel} · {e.subject}</div>
  </button>
);

/** Merge another enquiry from the same person into this one (or this one into it). */
export const EnquiryMergeDialog = ({ enq, onClose, onMerged }) => {
  const [cands, setCands] = useState(null);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [other, setOther] = useState(null);
  const [keepThis, setKeepThis] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/enquiries/${enq.id}/duplicates`).then((r) => setCands(r.data)).catch(() => setCands([])); }, [enq.id]);
  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); return undefined; }
    const t = setTimeout(() => api.get("/enquiries", { params: { q: q.trim(), limit: 10 } })
      .then((r) => setFound((r.data || []).filter((x) => x.id !== enq.id))).catch(() => setFound([])), 300);
    return () => clearTimeout(t);
  }, [q, enq.id]);
  const pick = (e) => { setOther(e); setKeepThis((enq.created_at || "") <= (e.created_at || "")); };
  const merge = async () => {
    const [keep, dup] = keepThis ? [enq, other] : [other, enq];
    setBusy(true);
    try {
      await api.post(`/enquiries/${keep.id}/merge`, { duplicate_id: dup.id });
      toast.success("Enquiries merged", { description: "They now count as one lead in the KPI report." });
      onMerged(keep.id);
    } catch (e) { toast.error("Merge failed", { description: e?.response?.data?.detail || e.message }); } finally { setBusy(false); }
  };
  const list = [...(cands || []), ...found.filter((f) => !(cands || []).some((c) => c.id === f.id))];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="enquiry-merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge duplicate enquiry</DialogTitle>
          <DialogDescription>Pick the other enquiry from the same person. The one you keep gets any missing name, email or
            mobile, the other's message and contact attempts; the other is archived.</DialogDescription>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search another name, email or phone…" data-testid="enquiry-merge-search" />
        <div className="max-h-64 space-y-1.5 overflow-auto">
          {cands === null ? <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Looking for duplicates…</div>
            : !list.length ? <p className="text-sm text-zinc-500" data-testid="enquiry-merge-none">No likely duplicates found — search above.</p>
              : list.map((e) => <Candidate key={e.id} e={e} picked={other?.id === e.id} onPick={pick} />)}
        </div>
        {other && (
          <div className="space-y-1.5 rounded-md border border-zinc-200 p-2.5 text-sm" data-testid="enquiry-merge-keep">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">Which one to keep?</div>
            {[[true, enq, "This enquiry"], [false, other, "The other enquiry"]].map(([v, e, label]) => (
              <label key={label} className="flex cursor-pointer items-start gap-2">
                <input type="radio" checked={keepThis === v} onChange={() => setKeepThis(v)} className="mt-1"
                  data-testid={`enquiry-merge-keep-${v ? "this" : "other"}`} />
                <span><b>{label}</b> — {who(e)} <span className="text-zinc-500">({fmtDateTime(e.created_at)})</span></span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={merge} disabled={!other || busy} data-testid="enquiry-merge-confirm">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <GitMerge className="mr-1.5 h-4 w-4" />}Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
